<template>
  <v-container>
    <ViewsContainer
      :moduleName="moduleName"
      :listViewComponent="Table"
      :filterComponent="Filter"
      :data="data"
      :config="'@/components/modules/clients/clients.config'"
      :onCreate="create"
      :onUpdate="update"
      :onDelete="remove"
      :exportCsv="exportCsv"
      :rowActionHandlers="rowActionHandlers"
      :show_toolbar="true"
      :show_pagination="true"
      :title="{singular:'Cliente', plural:'Clientes'}"
      :key="moduleName"
      :tabs="mergeMode ? mergeTagTabs : clientTypeTabs"
      :tabFilterKey="mergeMode ? 'tag' : 'client_type_id'"
      :showTabs="showClientTypeTabs || mergeMode"
      :selectable="mergeMode"
      :selectedItems="selectedClientIds"
      @update:selectedItems="onSelectedClientsChange"
    >
      <template #before-create>
        <div
          v-if="$can('clients.merge')"
          class="d-flex align-center mr-4 mt-2"
          style="gap: 10px;"
        >
          <v-btn
            v-if="!mergeMode"
            color="primary"
            variant="outlined"
            @click="enableMergeMode"
          >
            Fusionar
          </v-btn>

          <template v-else>
            <v-btn
              color="primary"
              :disabled="selectedClientIds.length < 2"
              @click="openMergeDialog"
            >
              Fusionar {{ selectedClientIds.length }} clientes
            </v-btn>
            <v-btn variant="text" @click="cancelMergeMode">
              Cancelar
            </v-btn>
          </template>
        </div>
      </template>
    </ViewsContainer>

    <v-dialog v-model="showMergeDialog" max-width="640">
      <v-card>
        <v-card-title class="text-h6">Confirmar Fusión de Clientes</v-card-title>
        <v-card-text>
          <p class="mb-3">
            Selecciona el cliente principal. Los demás clientes seleccionados serán fusionados en él.
          </p>
          <v-alert
            v-if="typeValidationError"
            type="warning"
            variant="tonal"
            class="mb-4"
          >
            {{ typeValidationError }}
          </v-alert>
          <v-select
            v-model="primaryClientId"
            :items="selectedClientOptions"
            item-title="label"
            item-value="id"
            label="Cliente principal"
            :disabled="isMerging"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" :disabled="isMerging" @click="showMergeDialog = false">Cancelar</v-btn>
          <v-btn
            color="primary"
            :loading="isMerging"
            :disabled="!canConfirmMerge"
            @click="confirmMerge"
          >
            Confirmar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script lang="ts" setup>
import { onMounted, computed, ref, watch } from 'vue';
import { usePreferencesStore } from '@/store/preferences';
import { useClientsStore } from '@/store/clients';
import { useModuleLayoutFetcher } from '@/composables/useModuleLayoutFetcher';
import { HEADERS, DEFAULT_HEADER_ORDER } from '@/components/modules/clients/clients.config';
import { useSendFileDialogStore } from '@/store/sendFileDialog';
import { useSendersStore } from '@/store/senders';
import { CARD_FIELDS, DEFAULT_CARD_ORDER } from '@/components/modules/sales/sales.config';
import { useDisplay } from 'vuetify';
import { remoteOptionsRegistry } from '@/lib/remoteOptions';
import { useAlertStore } from '@/store/alertStore';
import { useLayoutField } from '@/composables/useLayoutField';

const { mobile } = useDisplay();
const isMobile = computed(() => mobile.value);

import Table from '@/components/views/TableView.vue';
import MobileCardsView from '@/components/views/MobileCardsView.vue';
import Filter from '@/components/drawer/Filters.vue';
import ViewsContainer from '@/components/views/ViewsContainer.vue';

const moduleName = 'Clients';
const preferencesStore = usePreferencesStore();
const clientsStore = useClientsStore();
const alertStore = useAlertStore();
const sendFileDialogStore = useSendFileDialogStore();
const sendersStore = useSendersStore();

const getHostnameSafe = (): string => {
  if (typeof window !== 'undefined' && window.location?.hostname) {
    return window.location.hostname;
  }
  return process.env.VITE_PUBLIC_HOST ?? process.env.SSR_HOST ?? '';
};
const IS_DOMINIOMEDIOS = getHostnameSafe() === 'crm.dominiomedios.com';

const showClientTypeTabs = !IS_DOMINIOMEDIOS;
const clientTypeTabs = ref<Array<{ label: string; value: any }>>(
  showClientTypeTabs ? [{ label: 'Todos', value: 'all' }] : []
);
const mergeTagTabs = ref<Array<{ label: string; value: string }>>([
  { label: 'Agencia', value: 'Agencia' },
  { label: 'Cuenta', value: 'Cuenta' },
]);
const mergeTabValues = ['Agencia', 'Cuenta'] as const;
const currentView = useLayoutField(moduleName, 'currentView');
const filters = useLayoutField(moduleName, 'filters');
const mergeSelectedTag = useLayoutField(moduleName, 'mergeSelectedTag');

const {
  create,
  update,
  remove,
  exportCsv,
  reload
} = useModuleLayoutFetcher(
  moduleName,
  (params) => clientsStore.fetchClients(params),
  {
    create: clientsStore.createClient,
    update: (data) => clientsStore.updateClient(data.id, data),
    remove: clientsStore.deleteClient,
    exportCsv: clientsStore.exportCSV
  }
);

onMounted(async () => {
  preferencesStore.currentModule = moduleName;
  // Registra los headers y defaults para ESTE módulo:
  preferencesStore.setAllModuleHeaders(moduleName, HEADERS);
  preferencesStore.setDefaultModuleHeaders(
    moduleName,
    DEFAULT_HEADER_ORDER
      .map(value => HEADERS.find(header => header.value === value))
      .filter(Boolean) as typeof HEADERS
  );

  if (showClientTypeTabs && remoteOptionsRegistry.getClientTypes) {
    const types = await remoteOptionsRegistry.getClientTypes('');
    clientTypeTabs.value = [
      { label: 'Todos', value: 'all' },
      ...types.map((t: any) => ({ label: t?.name ?? t?.label ?? 'Sin nombre', value: t?.id ?? t }))
    ];
  }

  // Reset transient merge state on mount to avoid persisted localStorage lockups.
  mergeMode.value = false;
  mergeSelectedTag.value = 'Agencia';
  selectedClientIds.value = [];
  showMergeDialog.value = false;
  primaryClientId.value = null;

  // Normal mode defaults after hard refresh:
  // - clear merge-only filter
  // - keep selected type if exists, otherwise default to "Todos"
  const next = { ...(filters.value || {}) } as Record<string, any>;
  delete next.tag;
  if (next.client_type_id === 'all') delete next.client_type_id;
  filters.value = next;
});

const applyMergeTypeFilter = (base: Record<string, any>, tag: string) => {
  const next = { ...base };
  // Keep UI tab state stable for Toolbar selection.
  next.tag = tag;
  delete next.client_type_id;
  return next;
};

const sendMediaKit = (item:any) => {
  sendFileDialogStore.openForm({
    formTitle: 'Enviar Media Kit',
    main_email: item?.attributes?.email,
    client_id: item?.id,
    onSendEmail: sendersStore.sendEmailWithMediaKit,
    onSendWhatsapp: sendersStore.sendWhatsAppWithMediaKit,
  });
};

const rowActionHandlers = {
  sendMediaKit
};

const data = computed(() => clientsStore.clients);

const mergeMode = useLayoutField(moduleName, 'mergeMode');
const selectedClientIds = ref<Array<number | string>>([]);
const showMergeDialog = ref(false);
const primaryClientId = ref<number | null>(null);
const isMerging = ref(false);

const selectedClients = computed(() => {
  const selectedSet = new Set(selectedClientIds.value.map(Number));
  return clientsStore.clients.filter((client: any) => selectedSet.has(Number(client.id)));
});

const selectedClientOptions = computed(() =>
  selectedClients.value.map((client: any) => ({
    id: Number(client.id),
    label: `${client.attributes?.commercial_name || client.attributes?.legal_name || 'Cliente'} (#${client.id})`,
    clientTypeId: client.attributes?.client_type?.id ?? null,
    clientTypeTag: client.attributes?.client_type?.tag ?? null,
  }))
);

const typeValidationError = computed(() => {
  if (selectedClientOptions.value.length < 2) return '';
  const typeIds = new Set(selectedClientOptions.value.map((c: any) => c.clientTypeId));
  const typeTags = new Set(selectedClientOptions.value.map((c: any) => c.clientTypeTag).filter(Boolean));

  if (typeIds.size > 1 || typeTags.size > 1) {
    return 'Solo puedes fusionar clientes del mismo tipo: Agencia con Agencia o Cuenta con Cuenta.';
  }
  return '';
});

const canConfirmMerge = computed(() =>
  selectedClientIds.value.length >= 2 &&
  !!primaryClientId.value &&
  !typeValidationError.value
);

const enableMergeMode = () => {
  mergeMode.value = true;
  selectedClientIds.value = [];
  currentView.value = 'table';
  const next = { ...(filters.value || {}) };
  mergeSelectedTag.value = mergeTabValues.includes(mergeSelectedTag.value as any)
    ? mergeSelectedTag.value
    : 'Agencia';
  filters.value = applyMergeTypeFilter(next as Record<string, any>, mergeSelectedTag.value as string);
};

const cancelMergeMode = () => {
  mergeMode.value = false;
  selectedClientIds.value = [];
  showMergeDialog.value = false;
  primaryClientId.value = null;
  const next = { ...(filters.value || {}) };
  delete next.tag;
  delete (next as Record<string, any>).client_type_id;
  filters.value = next;
  mergeSelectedTag.value = 'Agencia';
};

watch(mergeMode, (enabled) => {
  if (enabled) {
    const current = { ...(filters.value || {}) } as Record<string, any>;
    const tag = mergeTabValues.includes(mergeSelectedTag.value as any)
      ? (mergeSelectedTag.value as string)
      : 'Agencia';
    filters.value = applyMergeTypeFilter(current, tag);
    return;
  }

  if (!enabled) {
    const next = { ...(filters.value || {}) } as Record<string, any>;
    delete next.tag;
    delete next.client_type_id;
    filters.value = next;
  }
});

watch(
  () => filters.value?.tag,
  (tagVal) => {
    if (!mergeMode.value) return;
    const next = { ...(filters.value || {}) } as Record<string, any>;
    const incomingTag = tagVal as string | undefined;
    if (incomingTag && mergeTabValues.includes(incomingTag as any) && mergeSelectedTag.value !== incomingTag) {
      mergeSelectedTag.value = incomingTag;
    }
    const selected = mergeSelectedTag.value && mergeTabValues.includes(mergeSelectedTag.value as any)
      ? (mergeSelectedTag.value as string)
      : 'Agencia';
    const merged = applyMergeTypeFilter(next, selected);
    if (JSON.stringify(merged) !== JSON.stringify(next)) {
      filters.value = merged;
    }
  }
);

const onSelectedClientsChange = (items: Array<number | string>) => {
  selectedClientIds.value = (items || []).map((item: any) =>
    typeof item === 'object' && item !== null ? Number(item.id) : Number(item)
  );
};

const openMergeDialog = () => {
  if (selectedClientIds.value.length < 2) return;
  primaryClientId.value = Number(selectedClientIds.value[0]);
  showMergeDialog.value = true;
};

const confirmMerge = async () => {
  if (!canConfirmMerge.value || !primaryClientId.value) return;
  try {
    isMerging.value = true;
    await clientsStore.mergeClients({
      client_ids: selectedClientIds.value.map(Number),
      primary_client_id: Number(primaryClientId.value),
    });

    showMergeDialog.value = false;
    cancelMergeMode();
    await reload();
    alertStore.showAlert('Los clientes seleccionados fueron fusionados correctamente.', 'success');
  } catch (error: any) {
    alertStore.showAlert(
      error?.response?.data?.message || 'Ocurrió un error al fusionar clientes.',
      'error'
    );
  } finally {
    isMerging.value = false;
  }
};
</script>
